import posthog from "posthog-js";
import { useEffect } from "react";

import { getPollDelayMs } from "@/utils/hooks/statusPolling/getPollDelayMs";

// Enough failures in a row to be an outage rather than a blip; reported to PostHog once per outage so a broken status action is visible without the loop ever giving up.
const CONSECUTIVE_FAILURES_BEFORE_REPORT = 5;

// Polls checkStatus at baseDelayMs while isRunning(pollInput) holds. A check that reports a change is expected to revalidate the page, which hands the effect a new pollInput and restarts the loop. Failures back the cadence off towards the cap but never stop it, so a transient outage (wifi drop, laptop sleep, a deploy) heals itself; when the browser reports the connection back or the tab is shown again the next poll runs right away instead of waiting out the backoff. isRunning and checkStatus are effect dependencies, so callers define them at module level (they only need pollInput) rather than inline, or the effect restarts every render.
export const useStatusPolling = <PollInput>({
  pollInput,
  isRunning,
  checkStatus,
  baseDelayMs,
  statusLabel,
}: {
  pollInput: PollInput | null;
  isRunning: (input: PollInput) => boolean;
  checkStatus: (input: PollInput) => Promise<boolean>;
  baseDelayMs: number;
  statusLabel: string;
}) => {
  useEffect(() => {
    if (pollInput === null || !isRunning(pollInput)) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let consecutiveFailures = 0;
    // Cleanup can only clear a pending timer. A poll that is mid-await when the effect tears down lands afterwards and would schedule a timer nothing can reach — an immortal loop polling from a dead component.
    let isCancelled = false;

    const poll = async () => {
      timer = null;
      let changed = false;
      try {
        changed = await checkStatus(pollInput);
        consecutiveFailures = 0;
      } catch (error) {
        consecutiveFailures++;
        console.warn(`Error checking ${statusLabel} status:`, error);
        if (consecutiveFailures === CONSECUTIVE_FAILURES_BEFORE_REPORT) {
          posthog.captureException(error, { statusLabel, consecutiveFailures });
        }
      }

      if (isCancelled || changed) {
        return;
      }
      timer = setTimeout(
        poll,
        getPollDelayMs(baseDelayMs, consecutiveFailures)
      );
    };

    // Only while backing off: a healthy loop keeps its cadence, a poll already in flight is left alone, and a hidden tab waits for the visibilitychange that follows the online event.
    const pollNowIfBackingOff = () => {
      if (
        document.visibilityState === "hidden" ||
        consecutiveFailures === 0 ||
        timer === null
      ) {
        return;
      }
      clearTimeout(timer);
      consecutiveFailures = 0;
      poll();
    };
    window.addEventListener("online", pollNowIfBackingOff);
    document.addEventListener("visibilitychange", pollNowIfBackingOff);

    poll();

    return () => {
      isCancelled = true;
      if (timer !== null) {
        clearTimeout(timer);
      }
      window.removeEventListener("online", pollNowIfBackingOff);
      document.removeEventListener("visibilitychange", pollNowIfBackingOff);
    };
  }, [pollInput, isRunning, checkStatus, baseDelayMs, statusLabel]);
};
